import * as React from 'react';
import {
    ContextualMenu, DefaultButton, getId, Icon, IDragOptions,
    Link, List, Modal, Panel, PanelType, SearchBox
} from '@fluentui/react';
import { DataUtils } from '../../common/views/DataUtils';
import { StyleUtils } from '../../common/views/StyleUtils';
import { Utils } from '../../common/views/Utils';
import { ShortWebsiteModel } from '../models/WebsiteModel';
import { ChangeWebsiteOrderModel } from '../../orders/models/OrderModel';
import { PopUpType } from '../../home/models/OtherTypes';
import { useWebsitePickerVM } from '../viewmodels/WebistePickerVM';

type WebsitePickerProps = {
    type: PopUpType;
    options: ShortWebsiteModel[];
    onValueChange: (field: keyof ChangeWebsiteOrderModel, value: any) => void;
}

export default function ChangeOrderWebsiteView({ onValueChange, options, type }: WebsitePickerProps) {
    const titleId: string = getId('title');
    const subtitleId: string = getId('subText');
    const dragOptions: IDragOptions = {
        moveMenuItemText: 'Move',
        closeMenuItemText: 'Close',
        menu: ContextualMenu
    };

    const width2 = Utils.getColumnWidth(2);
    const width10 = Utils.getColumnWidth(10);
    const vm = useWebsitePickerVM(options, type);

    const onSelectedChange = (option: ShortWebsiteModel) => {
        vm.onSelectedChange(option);
        onValueChange("websiteId", option.id)
    }

    const onClear = () => {
        vm.onClear();
        onValueChange("websiteId", undefined);
    }

    const onRecentClick = (item: any) => {
        vm.onRecentClick(item);
        onValueChange("websiteId", item.value);
    }

    const searchBox = (): JSX.Element => {
        return <SearchBox
            autoFocus={false}
            placeholder="Search"
            onSearch={newValue => vm.onTextChange(newValue)}
            onClear={() => vm.onTextChange("")}
            styles={{ root: { width: "100%", maxWidth: 500 } }}
            className="pb-10"
            underlined={true}
        />
    }

    const modalHeader = () => {
        return <div className="ms-Grid">
            <div className="ms-Grid-row">
                <div className={width10}><b>Websites</b></div>
                <div className={width2 + " right"}>
                    <Icon
                        iconName="ChromeClose"
                        className="pointer"
                        onClick={() => vm.setShowMenu(false)}
                    />
                </div>
            </div>
        </div>;
    }

    const modalView = () => {
        return <>
            <Modal
                titleAriaId={titleId}
                subtitleAriaId={subtitleId}
                isOpen={vm.showMenu}
                onDismiss={() => vm.setShowMenu(false)}
                isBlocking={true}
                dragOptions={dragOptions}
                styles={StyleUtils.getModalWidthStyles(450)}
            >
                <div className="popup-header">
                    {modalHeader()}
                </div>
                <div
                    id={subtitleId}
                    className="popup-body"
                    style={{ height: "80vh", overflowY: "auto" }}
                >
                    {contentView()}
                </div>
            </Modal>
        </>;
    }

    const panelView = () => {
        return <>
            <Panel
                isOpen={vm.showMenu}
                isBlocking={false}
                isLightDismiss={true}
                type={PanelType.custom}
                customWidth={DataUtils.PANEL_WIDTH}
                onDismiss={() => vm.setShowMenu(false)}
                closeButtonAriaLabel="Close"
                styles={{
                    content: {
                        paddingLeft: "20px !important",
                        paddingRight: "20px !important"
                    }
                }}
            >
                {contentView()}
            </Panel>
        </>;
    }

    const contentView = () => {
        return <>
            <div>
                <div className="pb-10"><b>Recent Selections: </b></div>
                {vm.recentItems.map((item: any) => {
                    return <>
                        <DefaultButton
                            text={item.text}
                            className="filter-text"
                            style={{ height: 25 }}
                            onClick={() => onRecentClick(item)}
                        />
                    </>;
                })}
            </div>

            {searchBox()}

            <div className="pv-10">
                {alphabetElement()}
            </div>

            <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
                <List
                    items={vm.filteredOptions}
                    key={vm.listHash}
                    onRenderCell={onRenderRow}
                />
            </div>
        </>;
    }

    const onRenderRow = (item?: ShortWebsiteModel, index?: number) => {
        if (!item) return <></>;
        if (!index) index = 0;

        const background = index % 2 === 0 ? " blue-background" : "";

        return <>
            <div
                className={"brand-item-row" + background}
                onClick={() => onSelectedChange(item)}>
                {item.name} {item.earningPointRate > 0 && <>({item.earningPointRate}%)</>}
            </div>
        </>;
    }

    const alphabetElement = () => {
        const obj: any = {};

        if (options) {
            for (let i = 0; i < options.length; i++) {
                const letter = options[i].name.substr(0, 1).toUpperCase();
                obj[letter] = letter;
            }
        }

        const list = [];

        for (let key in obj) {
            list.push(key);
        }

        return <div>
            {list.map((value: string) => <div className="brand-letter">
                <Link onClick={() => vm.filteredByLetter(value)}>{value}</Link>
            </div>)}

            <div className="brand-letter">
                <Link onClick={() => vm.onTextChange("")}>ALL</Link>
            </div>
        </div>
    }

    return <>
        <div className="pv-5">
            <label className="pr-5">Website</label>
            <Icon className="pointer" iconName="cancel" onClick={onClear} />
        </div>
        <div className="brand-dropdown" onClick={() => vm.setShowMenu(true)}>
            <div className="brand-dropdown-text">{vm.text}</div>
            <div className="brand-dropdown-icon"><Icon iconName="ChevronDown" /></div>
        </div>

        {type === PopUpType.modal && modalView()}
        {type === PopUpType.panel && panelView()}
    </>;
}